import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {useCoordinateZooming} from '../WorkspaceDrawer/Settings/hooks';

export type SharedPanelZoomState = {
  xAxis: string;
  xMin: number | null;
  xMax: number | null;
};
type SharedPanelZoomContextType = {
  isSharingZoom: boolean;
  setSharedZoom: (args: Partial<SharedPanelZoomState>) => void;
} & SharedPanelZoomState;

export const SharedPanelZoomContext = createContext<SharedPanelZoomContextType>(
  {
    isSharingZoom: false,
    setSharedZoom: () => {},
    xAxis: '',
    xMin: null,
    xMax: null,
  }
);

// Holds only the setter, which never changes. Components that only set the
// shared zoom read this context, so a shared zoom change does not re-render
// them.
const SharedPanelZoomSetterContext = createContext<
  SharedPanelZoomContextType['setSharedZoom']
>(() => {});

export const SharedPanelZoomContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const coordinateZooming = useCoordinateZooming();
  const [sharedZoom, setZoom] = useState<SharedPanelZoomState>({
    xAxis: '',
    xMin: null,
    xMax: null,
  });

  const setSharedZoom: SharedPanelZoomContextType['setSharedZoom'] =
    useCallback(args => {
      setZoom(prev => ({
        ...prev,
        ...args,
      }));
    }, []);

  const value = useMemo(
    () => ({
      ...sharedZoom,
      setSharedZoom,
      isSharingZoom: coordinateZooming ?? false,
    }),
    [sharedZoom, setSharedZoom, coordinateZooming]
  );

  return (
    <SharedPanelZoomSetterContext.Provider value={setSharedZoom}>
      <SharedPanelZoomContext.Provider value={value}>
        {children}
      </SharedPanelZoomContext.Provider>
    </SharedPanelZoomSetterContext.Provider>
  );
};

export const useSharedPanelZoom = () => {
  const context = useContext(SharedPanelZoomContext);
  if (!context) {
    throw new Error(
      'useSharedPanelZoom must be used within a SharedPanelZoomContextProvider'
    );
  }
  return context;
};

/**
 * Returns the shared zoom setter without subscribing to the shared zoom value.
 * Use it when a component only sets the shared zoom.
 */
export function useSetSharedPanelZoom(): SharedPanelZoomContextType['setSharedZoom'] {
  return useContext(SharedPanelZoomSetterContext);
}
